// Intake — the human review queue half of N-094 ("forward and file"). When
// the classifier is not confident enough to file an email on its own (as a
// license or a Communication), it lands here for OWNER/DIRECTOR to confirm
// or dismiss. Server-gated OWNER/DIRECTOR only; a MANAGER's GET 403s, which
// callers treat the same way ComplianceAlerts treats an unmapped role — no
// access, render nothing, never a visible error.
import { authedGet, authedWrite } from './apiClient';

export const INTAKE_KEY = 'intake';

export type IntakeGuessedKind = 'license' | 'communication' | 'vendor' | 'unknown';

export interface IntakeItem {
  id: number;
  subject: string | null;
  fromAddress: string | null;
  receivedAt: string | null;
  guessedKind: IntakeGuessedKind | null;
  guessedClinic: string | null;
  guessedFields: Record<string, unknown>;
  /** 0..1, or null when the classifier was not configured for this item. */
  confidence: number | null;
  reason: string;
  emailLink: string | null;
}

export interface IntakeData {
  items: IntakeItem[];
}

const REASON_LABELS: Record<string, string> = {
  low_confidence: 'low confidence',
  clinic_unresolved: 'clinic unclear',
  vendor_not_built: 'vendor (not yet automated)',
  unknown_kind: 'unrecognized',
  classifier_not_configured: 'needs setup',
};

/** A short human label for the server's `reason` code. An unrecognised code
 * (standing rule 13 — failures stay loud) is shown with its underscores
 * turned to spaces rather than silently swallowed into a generic string. */
export function humanizeIntakeReason(reason: string): string {
  return REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');
}

export function fetchIntake(): Promise<IntakeData> {
  return authedGet('/api/v1/intake');
}

export interface IntakeConfirmBody {
  kind: 'license' | 'communication';
  locationCode?: string;
  fields?: Record<string, unknown>;
}

export interface IntakeConfirmResult {
  ok: true;
  filedAs: 'license' | 'communication';
  status?: 'FILED' | 'NEEDS_REVIEW';
  /** Set when a license confirm itself landed as NEEDS_REVIEW (e.g. Drive
   * filing failed even after confirmation) — a warning to surface, not a
   * thrown error; the confirm still succeeded. */
  message?: string;
}

export async function confirmIntakeItem(id: number, body: IntakeConfirmBody): Promise<IntakeConfirmResult> {
  return authedWrite('POST', `/api/v1/intake/${id}/confirm`, body);
}

export async function dismissIntakeItem(id: number): Promise<void> {
  await authedWrite('POST', `/api/v1/intake/${id}/dismiss`, {});
}
